import {NgTemplateOutlet} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {Grid, GridCell, GridCellWidget, GridRow} from '@angular/aria/grid';
import type {Dayjs} from 'dayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

import {
  AriaCalendarDay,
  AriaCalendarWeek,
  AriaPickerMonthCell,
  AriaPickerYearCell,
  ARIA_DATE_ISO_FORMAT,
  buildCalendarWeeks,
  buildMonthCells,
  buildYearCells,
  initialPanelView,
  isDateSelectable,
  isDayInCalendarGrid,
  weekdayLabels,
} from '../utils/aria-date-picker.utils';
import { AriaDatePickerGranularity, AriaDatePickerPanelView, AriaDatePickerSelection } from '../models/aria-date-picker.model';

dayjs.locale('it');

/**
 * Pannello calendario completo: giorni, mesi, anni, doppio calendario.
 * Usato da {@link AriaDatePickerFieldComponent}.
 */
@Component({
  selector: 'app-aria-date-picker-panel',
  standalone: true,
  imports: [NgTemplateOutlet, Grid, GridRow, GridCell, GridCellWidget],
  templateUrl: './aria-date-picker-panel.component.html',
  styleUrl: './aria-date-picker-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AriaDatePickerPanelComponent {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly selection = input<AriaDatePickerSelection | null>(null);
  readonly minDate = input<Dayjs | null>(null);
  readonly maxDate = input<Dayjs | null>(null);
  readonly viewMonth = input.required<Dayjs>();
  /** Due mesi affiancati (ex `double` legacy). */
  readonly doubleCalendar = input(false);
  /** Permette drill-down mese/anno nel header. */
  readonly enableYearMonthNavigation = input(true);
  /** Granularità selezione: giorno, mese o anno. */
  readonly granularity = input<AriaDatePickerGranularity>('day');
  /** Anteprima visiva range tra prima e seconda data (solo mode range con draft attivo). */
  readonly enableRangePreview = input(false);

  readonly daySelected = output<Dayjs>();
  readonly monthChange = output<Dayjs>();
  readonly panelViewChange = output<AriaDatePickerPanelView>();

  protected readonly weekDays = weekdayLabels();
  protected readonly panelView = signal<AriaDatePickerPanelView>(
    initialPanelView('day', true)
  );
  protected readonly hoverDay = signal<Dayjs | null>(null);

  /** Mese mostrato nel calendario: aggiornato subito in locale, sincronizzato con l'input. */
  protected readonly displayViewMonth = signal(dayjs().startOf('month'));

  protected readonly secondaryViewMonth = computed(() =>
    this.displayViewMonth().add(1, 'month').startOf('month')
  );

  protected readonly weeks = computed(() =>
    buildCalendarWeeks(
      this.displayViewMonth(),
      this.selection(),
      this.minDate(),
      this.maxDate(),
      this.hoverDay(),
      this.doubleCalendar() ? this.secondaryViewMonth() : null,
      this.enableRangePreview(),
    )
  );

  protected readonly secondaryWeeks = computed(() =>
    buildCalendarWeeks(
      this.secondaryViewMonth(),
      this.selection(),
      this.minDate(),
      this.maxDate(),
      this.hoverDay(),
      this.doubleCalendar() ? this.displayViewMonth() : null,
      this.enableRangePreview(),
    )
  );

  protected readonly monthCells = computed(() =>
    buildMonthCells(this.displayViewMonth(), this.selection(), this.minDate(), this.maxDate())
  );

  protected readonly yearCells = computed(() =>
    buildYearCells(this.displayViewMonth(), this.selection(), this.minDate(), this.maxDate())
  );

  protected readonly monthRows = computed(() => chunkCells(this.monthCells(), 3));
  protected readonly yearRows = computed(() => chunkCells(this.yearCells(), 4));

  /** Primo giorno scelto, in attesa del secondo (anteprima hover attiva). */
  protected readonly isRangePicking = computed(() => {
    if (!this.enableRangePreview()) {
      return false;
    }
    const sel = this.selection();
    return !!sel?.start && !sel?.end;
  });

  protected readonly headerTitle = computed(() => {
    const view = this.panelView();
    const month = this.displayViewMonth();
    if (view === 'years') {
      const years = this.yearCells();
      if (!years.length) {
        return '';
      }
      return `${years[0].label}–${years[years.length - 1].label}`;
    }
    if (view === 'months') {
      return month.format('YYYY');
    }
    if (this.doubleCalendar()) {
      const next = this.secondaryViewMonth();
      return `${month.format('MMMM YYYY')} / ${next.format('MMMM YYYY')}`;
    }
    return month.format('MMMM YYYY');
  });

  private lastGranularity: AriaDatePickerGranularity | null = null;

  constructor() {
    effect(() => {
      this.displayViewMonth.set(this.viewMonth().startOf('month'));
    });

    effect(() => {
      const granularity = this.granularity();
      const navigation = this.enableYearMonthNavigation();
      if (granularity === this.lastGranularity) {
        return;
      }
      this.lastGranularity = granularity;
      this.panelView.set(initialPanelView(granularity, navigation));
    });
  }

  protected showMonthView(): void {
    if (!this.enableYearMonthNavigation() || this.doubleCalendar()) {
      return;
    }
    this.transitionView('months');
    queueMicrotask(() => this.focusPickerCell('months'));
  }

  protected showYearView(): void {
    if (!this.enableYearMonthNavigation() || this.doubleCalendar()) {
      return;
    }
    this.transitionView('years');
    queueMicrotask(() => this.focusPickerCell('years'));
  }

  protected showDayView(): void {
    this.transitionView('days');
    queueMicrotask(() => this.focusDayCell());
  }

  protected onPanelMouseDown(event: Event): void {
    const mouseEvent = event as MouseEvent;
    // Capture: evita blur sull'input del combobox prima che il bottone riceva il focus.
    mouseEvent.preventDefault();
  }

  protected previousPage(): void {
    const view = this.panelView();
    if (view === 'days') {
      this.setViewMonth(this.displayViewMonth().subtract(1, 'month'));
      return;
    }
    if (view === 'months') {
      this.setViewMonth(this.displayViewMonth().subtract(1, 'year'));
      return;
    }
    this.setViewMonth(this.displayViewMonth().subtract(12, 'year'));
  }

  protected nextPage(): void {
    const view = this.panelView();
    if (view === 'days') {
      this.setViewMonth(this.displayViewMonth().add(1, 'month'));
      return;
    }
    if (view === 'months') {
      this.setViewMonth(this.displayViewMonth().add(1, 'year'));
      return;
    }
    this.setViewMonth(this.displayViewMonth().add(12, 'year'));
  }

  protected onDayClick(event: MouseEvent, day: AriaCalendarDay): void {
    // Evita doppia emissione quando Enter genera anche un click sintetico.
    if (event.detail === 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.selectDay(day);
  }

  protected onDayKeydown(event: KeyboardEvent, day: AriaCalendarDay): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.selectDay(day);
      return;
    }

    if (!this.isCalendarNavigationKey(event.key)) {
      return;
    }

    const targetDate = this.resolveCalendarNavigationTarget(day.date, event);
    if (!targetDate) {
      return;
    }

    this.navigateToCalendarDay(event, targetDate);
  }

  protected onDaysGridArrowKeydown(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (!this.isCalendarNavigationKey(keyboardEvent.key)) {
      return;
    }

    const grid = keyboardEvent.currentTarget as HTMLElement;
    const dayKey = this.getActiveDayKeyFromGrid(grid, keyboardEvent);
    if (!dayKey) {
      return;
    }

    const currentDate = dayjs(dayKey).startOf('day');
    if (!currentDate.isValid()) {
      return;
    }

    const targetDate = this.resolveCalendarNavigationTarget(currentDate, keyboardEvent);
    if (!targetDate) {
      return;
    }

    this.navigateToCalendarDay(keyboardEvent, targetDate);
  }

  private isCalendarNavigationKey(key: string): boolean {
    return (
      key.startsWith('Arrow') ||
      key === 'Home' ||
      key === 'End' ||
      key === 'PageUp' ||
      key === 'PageDown'
    );
  }

  /**
   * Navigazione tastiera allineata a WAI-ARIA APG e Angular Material:
   * ↔ ±1 giorno, ↕ ±1 settimana, Page Up/Down ±1 mese, Shift+Page ±1 anno,
   * Home/End primo/ultimo giorno del mese.
   */
  private resolveCalendarNavigationTarget(
    date: Dayjs,
    event: KeyboardEvent
  ): Dayjs | null {
    switch (event.key) {
      case 'ArrowLeft':
        return date.subtract(1, 'day').startOf('day');
      case 'ArrowRight':
        return date.add(1, 'day').startOf('day');
      case 'ArrowUp':
        return date.subtract(7, 'day').startOf('day');
      case 'ArrowDown':
        return date.add(7, 'day').startOf('day');
      case 'Home':
        return date.startOf('month');
      case 'End':
        return date.endOf('month').startOf('day');
      case 'PageUp':
        return event.shiftKey
          ? date.subtract(1, 'year').startOf('day')
          : date.subtract(1, 'month').startOf('day');
      case 'PageDown':
        return event.shiftKey
          ? date.add(1, 'year').startOf('day')
          : date.add(1, 'month').startOf('day');
      default:
        return null;
    }
  }

  private navigateToCalendarDay(event: KeyboardEvent, targetDate: Dayjs): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (!isDateSelectable(targetDate, this.minDate(), this.maxDate())) {
      return;
    }

    const nextViewMonth = this.resolveViewMonthForTarget(targetDate);
    const monthChanged = !nextViewMonth.isSame(this.displayViewMonth(), 'month');

    if (monthChanged) {
      this.setViewMonth(nextViewMonth);
    }

    const targetKey = targetDate.format(ARIA_DATE_ISO_FORMAT);
    if (monthChanged) {
      requestAnimationFrame(() => {
        this.focusDayAfterNavigation(targetKey, true);
      });
      return;
    }

    queueMicrotask(() => {
      this.focusDayAfterNavigation(targetKey, false);
    });
  }

  protected onDaysGridKeydown(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') {
      return;
    }
    // Gestito sul bottone del giorno (focus sul widget).
    if ((keyboardEvent.target as HTMLElement).classList.contains('aria-calendar__day')) {
      return;
    }

    const grid = keyboardEvent.currentTarget as HTMLElement;
    const day = this.getActiveDayFromGrid(grid, keyboardEvent);
    if (!day || day.disabled) {
      return;
    }

    keyboardEvent.preventDefault();
    keyboardEvent.stopPropagation();
    this.selectDay(day);
  }

  private getActiveDayFromGrid(grid: HTMLElement, event?: KeyboardEvent): AriaCalendarDay | undefined {
    const dayKey = this.getActiveDayKeyFromGrid(grid, event);
    return dayKey ? this.findDayByKey(dayKey) : undefined;
  }

  private getActiveDayKeyFromGrid(grid: HTMLElement, event?: KeyboardEvent): string | null {
    const eventTarget = event?.target as HTMLElement | undefined;
    const eventCell = eventTarget?.closest('[data-day-key]');
    if (eventCell) {
      const key = eventCell.getAttribute('data-day-key');
      if (key) {
        return key;
      }
    }

    const focusedButton = grid.querySelector(
      '.aria-calendar__day:focus:not([disabled])'
    ) as HTMLButtonElement | null;
    const focusedKey = focusedButton?.closest('[data-day-key]')?.getAttribute('data-day-key');
    if (focusedKey) {
      return focusedKey;
    }

    const activeCell = grid.querySelector(
      'td.aria-calendar__cell[data-active="true"][data-day-key]'
    ) as HTMLElement | null;
    const activeCellKey = activeCell?.getAttribute('data-day-key');
    if (activeCellKey) {
      return activeCellKey;
    }

    const activeButton = grid.querySelector(
      '.aria-calendar__day[data-active="true"]:not([disabled])'
    ) as HTMLButtonElement | null;
    return activeButton?.closest('[data-day-key]')?.getAttribute('data-day-key') ?? null;
  }

  private resolveViewMonthForTarget(target: Dayjs): Dayjs {
    const currentView = this.displayViewMonth().startOf('month');
    const targetMonth = target.startOf('month');
    const targetKey = target.format(ARIA_DATE_ISO_FORMAT);

    if (this.isDayRenderedInCurrentView(targetKey)) {
      return currentView;
    }

    if (!this.doubleCalendar()) {
      return targetMonth;
    }

    const secondaryView = currentView.add(1, 'month');
    if (targetMonth.isBefore(currentView, 'month')) {
      return targetMonth;
    }
    if (targetMonth.isAfter(secondaryView, 'month')) {
      return targetMonth.subtract(1, 'month');
    }
    return currentView;
  }

  private isDayRenderedInCurrentView(dayKey: string): boolean {
    const selection = this.selection();
    const minDate = this.minDate();
    const maxDate = this.maxDate();
    const hover = this.hoverDay();

    if (
      isDayInCalendarGrid(
        this.displayViewMonth(),
        dayKey,
        selection,
        minDate,
        maxDate,
        hover,
        this.doubleCalendar() ? this.secondaryViewMonth() : null,
        this.enableRangePreview(),
      )
    ) {
      return true;
    }

    if (!this.doubleCalendar()) {
      return false;
    }

    return isDayInCalendarGrid(
      this.secondaryViewMonth(),
      dayKey,
      selection,
      minDate,
      maxDate,
      hover,
      this.displayViewMonth(),
      this.enableRangePreview(),
    );
  }

  private setViewMonth(month: Dayjs): void {
    const normalized = month.startOf('month');
    this.displayViewMonth.set(normalized);
    this.monthChange.emit(normalized);
  }

  private focusDayAfterNavigation(key: string, monthChanged: boolean, attempt = 0): void {
    const button = this.findFocusableDayButton(key);
    if (button) {
      button.focus({ preventScroll: true });
      return;
    }
    const maxAttempts = monthChanged ? 24 : 12;
    if (attempt < maxAttempts) {
      requestAnimationFrame(() => this.focusDayAfterNavigation(key, monthChanged, attempt + 1));
    }
  }

  private findFocusableDayButton(key: string): HTMLButtonElement | null {
    const grids = this.host.nativeElement.querySelectorAll(
      '.aria-calendar__grid'
    ) as NodeListOf<HTMLElement>;
    for (const grid of Array.from(grids)) {
      const button = grid.querySelector(
        `[data-day-key="${key}"] .aria-calendar__day:not([disabled])`
      ) as HTMLButtonElement | null;
      if (button) {
        return button;
      }
    }

    return this.host.nativeElement.querySelector(
      `[data-day-key="${key}"] .aria-calendar__day:not([disabled])`
    ) as HTMLButtonElement | null;
  }

  private getRenderedWeeks(): AriaCalendarWeek[] {
    return this.doubleCalendar()
      ? [...this.weeks(), ...this.secondaryWeeks()]
      : this.weeks();
  }

  private findDayByKey(key: string): AriaCalendarDay | undefined {
    for (const week of this.getRenderedWeeks()) {
      const match = week.days.find((d) => !d.hidden && d.key === key);
      if (match) {
        return match;
      }
    }
    return undefined;
  }

  private selectDay(day: AriaCalendarDay): void {
    if (day.disabled) {
      return;
    }
    this.daySelected.emit(day.date);
  }

  protected onMonthSelectedChange(selected: boolean, month: AriaPickerMonthCell): void {
    if (!selected || month.disabled) {
      return;
    }
    this.retainFocusInPanel();
    this.onMonthClick(month);
  }

  protected onYearSelectedChange(selected: boolean, year: AriaPickerYearCell): void {
    if (!selected || year.disabled) {
      return;
    }
    this.retainFocusInPanel();
    this.onYearClick(year);
  }

  protected onDayHover(day: AriaCalendarDay): void {
    if (day.disabled) {
      return;
    }
    this.hoverDay.set(day.date);
  }

  protected clearDayHover(): void {
    this.hoverDay.set(null);
  }

  protected onMonthClick(month: { date: Dayjs; disabled: boolean }): void {
    if (month.disabled) {
      return;
    }
    this.retainFocusInPanel();
    this.setViewMonth(month.date.startOf('month'));
    if (this.granularity() === 'month') {
      this.daySelected.emit(month.date.startOf('month'));
      return;
    }
    this.showDayView();
  }

  protected onYearClick(year: { date: Dayjs; disabled: boolean }): void {
    if (year.disabled) {
      return;
    }
    this.retainFocusInPanel();
    this.setViewMonth(year.date.startOf('year'));
    if (this.granularity() === 'year') {
      this.daySelected.emit(year.date.startOf('year'));
      return;
    }
    this.transitionView('months');
    queueMicrotask(() => this.focusPickerCell('months'));
  }

  protected dayButtonLabel(day: AriaCalendarDay): string {
    return day.date.format('dddd D MMMM YYYY');
  }

  /** Porta il focus sul giorno selezionato (o oggi) quando il popup si apre. */
  focusInitialSelection(preferredDate: Dayjs | null): void {
    const granularity = this.granularity();

    if (granularity === 'month') {
      this.panelView.set('months');
      queueMicrotask(() => this.focusSelectedMonthCell(preferredDate));
      return;
    }

    if (granularity === 'year') {
      this.panelView.set('years');
      queueMicrotask(() => this.focusSelectedYearCell(preferredDate));
      return;
    }

    if (this.panelView() !== 'days') {
      this.panelView.set('days');
    }

    const focusDate = (preferredDate ?? dayjs()).startOf('day');
    const key = focusDate.format(ARIA_DATE_ISO_FORMAT);

    const tryFocus = (attempt = 0): void => {
      const button =
        (this.host.nativeElement.querySelector(
          `[data-day-key="${key}"] .aria-calendar__day:not([disabled])`
        ) as HTMLButtonElement | null) ??
        (this.host.nativeElement.querySelector(
          '.aria-calendar__cell--range-start .aria-calendar__day:not([disabled])'
        ) as HTMLButtonElement | null) ??
        (this.host.nativeElement.querySelector(
          '.aria-calendar__day:not([disabled])'
        ) as HTMLButtonElement | null);

      if (button) {
        button.focus({ preventScroll: true });
        return;
      }
      if (attempt < 12) {
        requestAnimationFrame(() => tryFocus(attempt + 1));
      }
    };

    queueMicrotask(() => tryFocus());
  }

  private focusSelectedMonthCell(preferredDate: Dayjs | null): void {
    const month = (preferredDate ?? dayjs()).month();
    const button = this.host.nativeElement.querySelector(
      `.aria-panel__picker-grid--months .aria-panel__picker-cell:not([disabled])`
    ) as HTMLButtonElement | null;
    const cells = this.host.nativeElement.querySelectorAll(
      '.aria-panel__picker-grid--months .aria-panel__picker-cell:not([disabled])'
    ) as NodeListOf<HTMLButtonElement>;
    (cells[month] ?? button)?.focus();
  }

  private focusSelectedYearCell(preferredDate: Dayjs | null): void {
    const year = (preferredDate ?? dayjs()).year();
    const cells = this.host.nativeElement.querySelectorAll(
      '.aria-panel__picker-grid .aria-panel__picker-cell:not([disabled])'
    ) as NodeListOf<HTMLButtonElement>;
    const match = Array.from(cells).find((cell) => cell.textContent?.trim() === String(year));
    (match ?? cells[0])?.focus();
  }

  private transitionView(view: AriaDatePickerPanelView): void {
    this.retainFocusInPanel();
    this.panelView.set(view);
    this.panelViewChange.emit(view);
  }

  private retainFocusInPanel(): void {
    this.host.nativeElement.focus({ preventScroll: true });
  }

  private focusDayCell(): void {
    const day = this.host.nativeElement.querySelector(
      '.aria-calendar__day:not([disabled])'
    ) as HTMLElement | null;
    day?.focus();
  }

  private focusPickerCell(view: 'months' | 'years'): void {
    const selector =
      view === 'months'
        ? '.aria-panel__picker-grid--months .aria-panel__picker-cell:not([disabled])'
        : '.aria-panel__picker-grid .aria-panel__picker-cell:not([disabled])';
    (this.host.nativeElement.querySelector(selector) as HTMLElement | null)?.focus();
  }
}

function chunkCells<T>(items: T[], columns: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += columns) {
    rows.push(items.slice(i, i + columns));
  }
  return rows;
}
